import { setPageInfo } from "core/react-utils";
import { observer } from "mobx-react-lite";
import { Tab } from "semantic-ui-react";
import { TestResult, TestStatus } from "../../models/test-run";
import Spinner from "../spinner";
import TerminalOutput from "../terminal-output";
import { useMemo, useState, useCallback, useEffect } from "react";
import { useLogStream, formatLogMessage, LogStreamMessage } from "core/use-log-stream";

interface Props {
  testResult: TestResult;
}

// Create an ANSI box around text
const ansiBox = (title: string, color: string): string => {
  const width = title.length + 4;
  const top = `╔${"═".repeat(width)}╗`;
  const middle = `║  ${title}  ║`;
  const bottom = `╚${"═".repeat(width)}╝`;
  return `${color}${top}\n${middle}\n${bottom}\x1b[0m`;
};

const ConsoleTab = observer(({ testResult }: Props) => {
  setPageInfo("testResult", "Console");

  const isRunning = testResult.status === TestStatus.RUNNING;
  const hasError = testResult.error && testResult.error.length > 0;
  const testRunTestId = testResult.testRunTestId;

  // State for streaming log content
  const [streamedContent, setStreamedContent] = useState("");

  // Handle incoming log messages - filter to only show messages for this specific test
  const handleLogMessage = useCallback((message: LogStreamMessage) => {
    // Only show PROCESS_OUTPUT messages that include this test's ID
    if (message.logType === "PROCESS_OUTPUT") {
      const isForThisTest = message.testRunTestIds?.includes(testRunTestId);
      if (!isForThisTest) {
        return; // Skip messages not for this test
      }
    }

    const formattedLine = formatLogMessage(message);
    setStreamedContent((prev) => prev + (prev ? "\n" : "") + formattedLine);
  }, [testRunTestId]);

  // Use WebSocket streaming when test is running
  const { isConnected, isConnecting } = useLogStream({
    testRunId: testResult.testRunId,
    enabled: isRunning,
    onMessage: handleLogMessage,
  });

  // Clear streamed content when test run changes or stops running
  useEffect(() => {
    if (!isRunning) {
      setStreamedContent("");
    }
  }, [isRunning, testResult.testRunId]);

  // Combine all outputs into a single terminal view with ANSI color sections
  const combinedOutput = useMemo(() => {
    const parts: string[] = [];

    // Errors section (red box)
    if (hasError) {
      parts.push(ansiBox("ERROR", "\x1b[31m"));
      parts.push(testResult.error);
      if (testResult.stackTrace) {
        parts.push(`\n\x1b[33m--- Stacktrace ---\x1b[0m`);
        parts.push(testResult.stackTrace);
      }
    }

    // Test output section (if different from process output)
    const testOutput = (() => {
      if (!testResult.output) return "";
      if (!testResult.processOutput) return testResult.output.trim();
      return testResult.output.replace(testResult.processOutput, "").trim();
    })();

    if (testOutput.length > 0) {
      if (parts.length > 0) parts.push("");
      parts.push(ansiBox("TEST OUTPUT", "\x1b[32m"));
      parts.push(testOutput);
    }

    // Process output section (cyan)
    if (testResult.processOutput && testResult.processOutput.length > 0) {
      if (parts.length > 0) parts.push("");
      parts.push(`\x1b[36m=== PROCESS OUTPUT ===\x1b[0m`);
      parts.push(testResult.processOutput);
    }

    return parts.join("\n");
  }, [testResult.error, testResult.stackTrace, testResult.output, testResult.processOutput]);

  // Determine which content to show and whether to use streaming mode
  const displayContent = isRunning ? streamedContent : combinedOutput;
  const hasDisplayContent = displayContent.length > 0 || isRunning;

  return (
    <Tab.Pane>
      <div className="detail">
        {hasDisplayContent ? (
          <div>
            <TerminalOutput
              content={displayContent}
              height="calc(100vh - 420px)"
              initialScrollToTop={!isRunning}
              streaming={isRunning}
              isConnected={isConnected}
              isLoading={isConnecting}
            />
            {isRunning && !streamedContent && (
              <div id="loading-spinner" style={{ marginTop: "8px" }}>
                <Spinner text="Waiting for logs..." />
              </div>
            )}
          </div>
        ) : (
          <p>No output available</p>
        )}
      </div>
    </Tab.Pane>
  );
});

export default ConsoleTab;
